import { useLayoutEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { type NoteDraft } from "@/features/notes"
import { useAppDispatch, useAppSelector } from "@/redux/hooks.ts"
import { resetNotesDraft } from "@/redux/slices/notes-draft-slice.ts"
import { NEW_ENTITY } from "@/utils/types/misc-types.ts"

/**
 * useInitializeDraftNote
 * Hook to initialize the note draft
 * If the noteDraft is not present, it will reset the noteDraft with default values
 * If the animalId is present, it will reset the noteDraft with the animalId
 * If the noteDraft is present, it will return the noteDraft
 */
const useInitializeDraftNote = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const animalId = searchParams.get("animalId")
  const hiveId = searchParams.get("hiveId")
  const apiaryId = searchParams.get("apiaryId")
  const queenId = searchParams.get("queenId")
  const birdGroupId = searchParams.get("birdGroupId")
  const parcelId = searchParams.get("parcelId")
  const cropPlantingId = searchParams.get("cropPlantingId")

  const noteDraft = useAppSelector(
    (state) => state.notesDraftSlice.drafts["new"],
  )

  if (!noteDraft) {
    dispatch(resetNotesDraft({ id: "new" }))
  }

  useLayoutEffect(() => {
    let initialData: Partial<NoteDraft> = {}

    if (apiaryId) {
      initialData = { apiaries: [apiaryId] }
    } else if (hiveId) {
      initialData = { hives: [hiveId] }
    } else if (animalId) {
      initialData = { animals: [animalId] }
    } else if (queenId) {
      initialData = { queens: [queenId] }
    } else if (birdGroupId) {
      initialData = { birds_groups: [birdGroupId] }
    } else if (parcelId) {
      initialData = { parcels: [parcelId] }
    } else if (cropPlantingId) {
      initialData = { crop_plantings: [cropPlantingId] }
    }
    if (Object.keys(initialData).length > 0) {
      dispatch(resetNotesDraft({ id: NEW_ENTITY, draft: initialData }))
    }
  }, [animalId, apiaryId, birdGroupId, dispatch, hiveId, queenId])

  return { noteDraft }
}

export default useInitializeDraftNote
